import { useNavigate } from 'react-router-dom';
import { Avatar } from '@/components/Avatar/Avatar';
import { Button } from '@/components/Button/Button';
import { Chip } from '@/components/Chip/Chip';
import { t } from '@/lib/i18n';
import { formatPersonName } from '@/lib/format';
import type { Member } from '../../schemas';
import styles from './HireSuccess.module.css';

export interface AddMemberSuccessProps {
  member: Member;
}

export function AddMemberSuccess({ member }: AddMemberSuccessProps) {
  const navigate = useNavigate();
  const fullName = formatPersonName(member);
  const firstName = member.first_name;
  const unitName = member.units[0]?.name;
  const positionName = member.positions[0]?.name;

  return (
    <div className={styles.page} data-testid="hire-success">
      <span className={`${styles.confettiDot} ${styles.confettiDot1}`} />
      <span className={`${styles.confettiDot} ${styles.confettiDot2}`} />
      <span className={`${styles.confettiSquare} ${styles.confettiSquare1}`} />
      <span className={`${styles.confettiSquare} ${styles.confettiSquare2}`} />

      <div className={styles.avatarWrap}>
        <Avatar name={fullName} size="lg" kind="ai" />
      </div>
      <div className={styles.addedLabel}>{t('ADDED')}</div>
      <h1 className={styles.title}>
        {t('Welcome to the team, ')}
        <span className={styles.titleName}>{firstName}.</span>
      </h1>
      <p className={styles.subtitle}>
        {t(
          '{name} is ready to start. Brief them with a job, or chat to introduce them to the team.',
          {
            name: fullName,
          },
        )}
      </p>

      <div className={styles.positionCard}>
        <div className={styles.basicsHead}>
          <span className={styles.basicsName}>{fullName}</span>
          <Chip variant="neutral" size="sm">
            {t('AI')}
          </Chip>
        </div>
        {unitName || positionName ? (
          <div className={styles.basicsPills}>
            {unitName ? (
              <Chip variant="neutral" size="sm">
                {unitName}
              </Chip>
            ) : null}
            {positionName ? (
              <Chip variant="neutral" size="sm">
                {positionName}
              </Chip>
            ) : null}
          </div>
        ) : null}
        {member.personality ? (
          <>
            <div className={styles.briefLabel}>{t('HOW THEY WORK')}</div>
            <p className={styles.briefBody}>&ldquo;{member.personality}&rdquo;</p>
          </>
        ) : null}
      </div>

      <div className={styles.actions}>
        <Button type="button" variant="ghost" size="md" onClick={() => void navigate('/team/add')}>
          + {t('Add someone else')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="md"
          onClick={() => void navigate(`/team/m/${encodeURIComponent(member.id)}/chat`)}
        >
          💬 {t('Say hi to {name}', { name: firstName })}
        </Button>
        <Button
          type="button"
          variant="primary"
          size="md"
          onClick={() => void navigate(`/team/m/${encodeURIComponent(member.id)}`)}
        >
          {t("Visit {name}'s profile", { name: firstName })} →
        </Button>
      </div>
    </div>
  );
}
